import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSamlResponse } from './hooks/useSamlResponse';
import loadingImg from './loading.svg';
import { TenantMetadata } from './CustomTypes';
import './App.css';
import { useAuthCheck } from './hooks/useAuthCheck';

interface QueryParams {
  [key: string]: string;
}

const getQueryParams = (query: string): QueryParams => {
  return query
    .slice(1)
    .split('&')
    .reduce((params: QueryParams, param) => {
      const [key, value] = param.split('=');
      params[key] = decodeURIComponent(value);
      return params;
    }, {});
};

const BlackDiamondSSO: React.FC = () => {
  const { isAuthenticated, isLoading, user, tenants } = useAuthCheck();
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const baseUrl = process.env.REACT_APP_TENANT_BASE_URL || '';
  const organization = window.location.host
    .replace(baseUrl, '')
    .replace('.', '') || 'auth-portal';
  const tenantBySubdomain = tenants.find(tenant => tenant.alias === organization);
  const tenantMetadata: TenantMetadata | null = tenantBySubdomain?.metadata ? JSON.parse(tenantBySubdomain.metadata) as TenantMetadata : null;
  const settingsId = queryParams?.settingsId ?? tenantMetadata?.blackDiamondSettingsId;
  const [authError, setAuthError] = useState<string | null>(null);

  const { samlResponse, action, loading: samlLoading, error: samlError } = useSamlResponse({
    props: {
        tenantId: tenantBySubdomain?.tenantId,
        settingsId: settingsId,
        impersonate: queryParams?.impersonate
    }
  });

  useEffect(() => {
    if (samlError) {
        console.error('SAML Error:', samlError);
        return;
    } else if (tenants.length > 0 && !tenantBySubdomain) {
      console.error('Tenant not found:', organization);
      setAuthError(`You don't have access to this tenant.`);
      return;
    } else if (isAuthenticated && user && tenantBySubdomain && samlResponse && action) {
      console.log('Redirecting to Black Diamond...');
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action;
      form.target = '_self';
      form.style.display = 'none';

      const samlInput = document.createElement('input');
      samlInput.type = 'hidden';
      samlInput.name = 'SAMLResponse';
      samlInput.value = samlResponse;
      form.appendChild(samlInput);

      const relayStateInput = document.createElement('input');
      relayStateInput.type = 'hidden';
      relayStateInput.name = 'RelayState';
      relayStateInput.value = 'https://bd3.bdreporting.com';
      form.appendChild(relayStateInput);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
  }, [samlError, isAuthenticated, user, samlResponse, action, tenantBySubdomain, tenants, organization]);

  if (isLoading) {
    return <div><img src={loadingImg} alt="loading" width={200} height={200} /></div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (authError) {
    return (
      <div className="App">
        <header className="App-header">
          <h1>Authentication Error</h1>
          <p>{authError}</p>
        </header>
      </div>
    );
  }

  return (
    <div>
      <img src={loadingImg} alt="loading" width={200} height={200} />
      <br/>
      <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
        {samlLoading ? 'Loading SAML response...' :
         (!samlResponse || !action) ? 'Preparing to redirect to Black Diamond...' :
         'Redirecting to Black Diamond...'}
      </span>
    </div>
  );
};

export default BlackDiamondSSO;
